import { pillarIcons } from "./Iconography";

const pillars = [
  {
    name: "Own.",
    tagline: "Health Vault",
    description: "All your health sources in one encrypted, portable vault you control.",
    tone: "text-kin-green-dark",
    dot: "bg-kin-green",
    halo: "from-kin-green/22 to-kin-green/0",
    icon: pillarIcons.own,
  },
  {
    name: "Understand.",
    tagline: "AI Guides",
    description: "Expert health AI analyzes your longitudinal data for personalized insights.",
    tone: "text-kin-body",
    dot: "bg-kin-gold",
    halo: "from-kin-gold/22 to-kin-gold/0",
    icon: pillarIcons.understand,
  },
  {
    name: "Share.",
    tagline: "On Your Terms",
    description: "Send specific results to doctors or family, and revoke access anytime.",
    tone: "text-kin-dark",
    dot: "bg-kin-lavender",
    halo: "from-kin-lavender/22 to-kin-lavender/0",
    icon: pillarIcons.share,
  },
];

const voiceTraits = [
  {
    title: "Warm",
    text: "Human, supportive, and respectful.",
    icon: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z",
    dot: "bg-kin-green",
  },
  {
    title: "Clear",
    text: "Direct language with low ambiguity.",
    icon: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z",
    iconDetail: "M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    dot: "bg-kin-gold",
  },
  {
    title: "Privacy-first",
    text: "Your vault, your rules in every interaction.",
    icon: "M16.5 10.5V7.5a4.5 4.5 0 10-9 0v3m-1.5 0h12a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6a1.5 1.5 0 01-1.5-1.5v-6A1.5 1.5 0 016 10.5z",
    dot: "bg-kin-green",
  },
  {
    title: "Actionable",
    text: "Every insight should lead to a clear next step.",
    icon: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",
    dot: "bg-kin-lavender",
  },
];

export default function Philosophy() {
  return (
    <section
      id="philosophy"
      className="kin-section-glow px-6 py-14 scroll-mt-16 sm:px-8 sm:py-16 gradient-wash"
    >
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold tracking-[0.22em] uppercase text-kin-muted">
          01 - Philosophy
        </p>

        <div className="mt-6 max-w-3xl">
          <h2 className="font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
            Your Health, Intelligent.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-kin-body/90 sm:text-lg">
            Get expert health insights on your own personal data without giving up privacy. The
            experience should always feel trustworthy, personal, and in your control.
          </p>
        </div>

        <div className="mt-10 kin-surface-strong rounded-[24px] p-7 sm:p-10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-green-dark">
            Mission
          </p>
          <p className="mt-4 max-w-4xl font-serif text-2xl leading-snug text-kin-black sm:text-3xl">
            A unified health ecosystem powered by AI: upload your data, unlock personalized insight,
            and share what matters on your terms.
          </p>
        </div>

        <div className="mt-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">Pillars</p>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            {pillars.map((pillar) => (
              <article key={pillar.name} className="kin-surface rounded-[24px] p-6">
                <div className="flex items-center gap-4">
                  <div
                    className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-kin-olive/12 bg-kin-white/85 overflow-hidden`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${pillar.halo}`} />
                    <svg
                      className="relative h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d={pillar.icon.path} className="text-kin-dark" stroke="currentColor" />
                      {pillar.icon.detail && (
                        <path
                          d={pillar.icon.detail}
                          className={pillar.icon.tone}
                          stroke="currentColor"
                        />
                      )}
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-kin-black">{pillar.name}</h3>
                </div>
                <p className={`mt-2 text-sm font-semibold ${pillar.tone}`}>{pillar.tagline}</p>
                <p className="mt-3 text-sm leading-relaxed text-kin-body/90">
                  {pillar.description}
                </p>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-12 kin-surface rounded-[24px] p-6 sm:p-8">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">Voice</p>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-kin-body/90">
            Voice is calm and clear. Messaging should reduce anxiety, increase confidence, and make
            complex health information feel understandable without oversimplification.
          </p>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {voiceTraits.map((trait) => (
              <div key={trait.title} className="kin-surface rounded-[20px] p-4">
                <div className="flex items-center gap-2.5 mb-2">
                  <div
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${trait.dot}/12`}
                  >
                    <svg
                      className="h-4 w-4 text-kin-dark"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d={trait.icon} />
                      {trait.iconDetail && <path d={trait.iconDetail} />}
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-kin-black">{trait.title}</p>
                </div>
                <p className="text-xs leading-relaxed text-kin-body/80">{trait.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 kin-surface rounded-[24px] p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-green-dark">
                Platform &amp; Skill
              </p>
              <p className="mt-2 font-serif text-xl font-bold text-kin-black">
                In product moments, the skill leads. Kin curates.
              </p>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-kin-body/85">
                Kin is the trust container, marketplace, vault, and entry point. But inside a skill
                view, the platform steps back completely. How this shapes color, visual hierarchy,
                and language is defined in section 04.
              </p>
            </div>
            <a
              href="#platform-skill"
              className="shrink-0 rounded-xl border border-kin-olive/14 bg-kin-white/70 px-4 py-2 text-xs font-semibold text-kin-body transition hover:bg-kin-white"
            >
              Section 04 →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
